import { getJson, getList, sendJson, type PaginationMeta } from '@/lib/api';

/**
 * Typed fetchers over /api/v3/submissions — the ops view of the two intake
 * pipelines: website-form leads and payment-request submissions. Shapes mirror
 * apps/api's submissions controller.
 */

// ─── Leads ──────────────────────────────────────────────────────────────────

/** A lead submission's derived processing status. */
export const LEAD_SUBMISSION_STATUS = {
  pending: 'pending',
  quarantined: 'quarantined',
  failed: 'failed',
  processed: 'processed',
} as const;
export type LeadSubmissionStatus =
  (typeof LEAD_SUBMISSION_STATUS)[keyof typeof LEAD_SUBMISSION_STATUS];

export interface LeadSubmissionRow {
  readonly id: string;
  readonly formKey: string | null;
  readonly name: string | null;
  readonly email: string | null;
  readonly company: string | null;
  readonly createdAt: string | null;
  readonly status: LeadSubmissionStatus;
  readonly leadSource: string | null;
  readonly channel: string | null;
  readonly salesEmailSent: boolean;
  readonly pardotHandlerStatus: number | null;
  readonly error: string | null;
  readonly spamVerdict: string | null;
}

export interface LeadSubmissionDetail extends LeadSubmissionRow {
  readonly fields: Record<string, unknown>;
  readonly context: Record<string, unknown>;
  readonly notifications: Record<string, unknown> | null;
  readonly spam: Record<string, unknown> | null;
  readonly updatedAt: string | null;
}

/** Query for the lead list. */
export interface LeadSubmissionListParams {
  readonly status?: LeadSubmissionStatus;
  readonly formKey?: string;
  /** Free text over the lead's name, email, company and form. */
  readonly search?: string;
  readonly page: number;
  readonly limit: number;
}

/** GET /submissions/leads. */
export function listLeadSubmissions(
  params: LeadSubmissionListParams,
  signal?: AbortSignal,
): Promise<{ items: LeadSubmissionRow[]; pagination: PaginationMeta }> {
  const query: Record<string, unknown> = { page: params.page, limit: params.limit };
  if (params.status) query.status = params.status;
  if (params.formKey) query.formKey = params.formKey;
  if (params.search) query.search = params.search;
  return getList<LeadSubmissionRow>('/submissions/leads', query, signal);
}

/** GET /submissions/leads/:id. */
export function getLeadSubmission(id: string, signal?: AbortSignal): Promise<LeadSubmissionDetail> {
  return getJson<LeadSubmissionDetail>(`/submissions/leads/${id}`, undefined, signal);
}

/** POST /submissions/leads/:id/reprocess. `release` bypasses the spam gate. */
export function reprocessLeadSubmission(
  id: string,
  release: boolean,
): Promise<{ id: string; queued: boolean; released: boolean }> {
  return sendJson('post', `/submissions/leads/${id}/reprocess`, { release });
}

// ─── Payment submissions ──────────────────────────────────────────────────────

export interface PaymentSubmissionRow {
  readonly id: string;
  readonly source: string;
  readonly form: string;
  readonly submissionId: string | null;
  readonly contractNumber: string | null;
  readonly vendorName: string | null;
  readonly vendorEmail: string | null;
  readonly total: number | null;
  readonly status: string;
  readonly error: string | null;
  readonly expensesCreated: boolean;
  readonly receivedAt: string;
}

export interface PaymentSubmissionOutcome {
  readonly status: string;
  readonly statementId?: string;
  readonly opportunityId?: string;
  readonly contactId?: string;
  readonly accountId?: string;
  readonly created?: number;
  readonly expenseIds?: string[];
  readonly expensesCreated?: boolean;
  readonly qboBillId?: string;
  readonly error?: string;
  readonly processedAt?: string;
}

export interface QboPushRecord {
  readonly submissionId: string;
  readonly form?: string;
  readonly qboBillId?: string;
  readonly status: string;
  readonly receiptsAttached?: number;
  readonly error?: string;
}

export interface PaymentSubmissionDetail extends PaymentSubmissionRow {
  readonly outcome: PaymentSubmissionOutcome | null;
  readonly body: Record<string, unknown> | null;
  readonly qbo: QboPushRecord | null;
}

/** Query for the payment list. */
export interface PaymentSubmissionListParams {
  readonly source?: string;
  readonly form?: 'facilitator' | 'assistant';
  readonly status?: string;
  /** Free text over the vendor, contract number and submission id. */
  readonly search?: string;
  readonly page: number;
  readonly limit: number;
}

/** GET /submissions/payments. */
export function listPaymentSubmissions(
  params: PaymentSubmissionListParams,
  signal?: AbortSignal,
): Promise<{ items: PaymentSubmissionRow[]; pagination: PaginationMeta }> {
  const query: Record<string, unknown> = { page: params.page, limit: params.limit };
  if (params.source) query.source = params.source;
  if (params.form) query.form = params.form;
  if (params.status) query.status = params.status;
  if (params.search) query.search = params.search;
  return getList<PaymentSubmissionRow>('/submissions/payments', query, signal);
}

/** GET /submissions/payments/:id. */
export function getPaymentSubmission(
  id: string,
  signal?: AbortSignal,
): Promise<PaymentSubmissionDetail> {
  return getJson<PaymentSubmissionDetail>(`/submissions/payments/${id}`, undefined, signal);
}

/** POST /submissions/payments/:id/reprocess. */
export function reprocessPaymentSubmission(
  id: string,
): Promise<{ id: string; outcome: PaymentSubmissionOutcome | null }> {
  return sendJson('post', `/submissions/payments/${id}/reprocess`);
}

/** A Salesforce record link (resolves in whichever org the viewer is in). */
export function salesforceRecordUrl(id: string): string {
  return `https://bestcorporateevents.lightning.force.com/${id}`;
}
